
import { toggleTheme, getCurrentTheme } from './theme.js';
import { getApiUrl } from './apiConfig.js';
import { refreshVisualization } from './refreshVisualization.js';
import { apiGet, apiPost } from './utils/apiClient.js';
import { getLocale, setLocale, loadLocale, t } from './utils/i18n.js';

type UserResponse = { user: { is_admin: boolean } };

export function setupSettings(): void {
    const settingsBtn = document.getElementById('settings-btn');
    const settingsModal = document.getElementById('settings-modal');
    const closeBtn = document.getElementById('close-settings-btn');
    const themeToggleBtn = document.getElementById('theme-toggle-btn');
    const logoutBtn = document.getElementById('logout-btn');
    const adminPanelBtn = document.getElementById('admin-panel-btn');
    const adminSection = document.getElementById('admin-section');
    
    // Language modal elements
    const languageBtn = document.getElementById('language-btn');
    const languageModal = document.getElementById('language-modal');
    const closeLanguageBtn = document.getElementById('close-language-btn');
    const languageModalTitle = document.getElementById('language-modal-title');
    const languageOptions = document.querySelectorAll('[data-lang]');

    if (!settingsBtn || !settingsModal || !closeBtn) return;

    // Initialize locale
    loadLocale();
    
    // Check if user is admin and show admin button
    checkAdminStatus();

    async function checkAdminStatus() {
        try {
            const data = await apiGet<UserResponse>(getApiUrl('/auth/user'));
            if (data.user && data.user.is_admin && adminSection) {
                adminSection.style.display = 'block';
            }
        } catch (error) {
            console.error('Failed to check admin status:', error);
        }
    }

    // Open settings modal
    settingsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        settingsModal.classList.remove('hidden');
        updateThemeToggleState();
    });

    // Close modal
    const closeModal = () => {
        settingsModal.classList.add('hidden');
    };

    closeBtn.addEventListener('click', closeModal);

    // Close on backdrop click
    settingsModal.addEventListener('click', (e) => {
        if (e.target === settingsModal) {
            closeModal();
        }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !settingsModal.classList.contains('hidden')) {
            closeModal();
        }
    });

    // Language modal
    if (languageBtn && languageModal && closeLanguageBtn) {
        // Set modal title from i18n
        if (languageModalTitle) {
            languageModalTitle.textContent = t('settings.selectLanguage');
        }
        
        // Open language modal
        languageBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            languageModal.classList.remove('hidden');
            updateLanguageSelection();
        });

        // Close language modal
        const closeLanguageModal = () => {
            languageModal.classList.add('hidden');
        };

        closeLanguageBtn.addEventListener('click', closeLanguageModal);

        // Close on backdrop click
        languageModal.addEventListener('click', (e) => {
            if (e.target === languageModal) {
                closeLanguageModal();
            }
        });

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !languageModal.classList.contains('hidden')) {
                closeLanguageModal();
            }
        });

        // Language selection
        languageOptions.forEach(option => {
            option.addEventListener('click', (e) => {
                const locale = (e.currentTarget as HTMLElement).getAttribute('data-lang');
                if (locale) {
                    setLocale(locale);
                    window.location.reload();
                }
            });
        });
    }

    // Update language selection state
    function updateLanguageSelection() {
        const currentLocale = getLocale();
        languageOptions.forEach(option => {
            const lang = (option as HTMLElement).getAttribute('data-lang');
            const btn = (option.tagName === 'BUTTON' ? option : option.closest('button'));
            if (btn) {
                if (lang === currentLocale) {
                    btn.classList.add('active');
                } else {
                    btn.classList.remove('active');
                }
            }
        });
    }

    // Theme toggle
    if (themeToggleBtn) {
        themeToggleBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const next = toggleTheme();
            updateThemeToggleState();
            
            // Refresh chart to apply new theme colors
            refreshVisualization();
        });
    }

    // Language selector (removed - now in separate modal)

    // Logout
    if (logoutBtn) {
        logoutBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            try {
                await apiPost('/auth/logout', {});
                window.location.href = '/login.html';
            } catch (error) {
                console.error('Logout failed:', error);
            }
        });
    }

    // Admin panel
    if (adminPanelBtn) {
        adminPanelBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            window.location.href = '/admin.html';
        });
    }

    function updateThemeToggleState(): void {
        if (!themeToggleBtn) return;
        
        const isDark = getCurrentTheme() === 'dark';
        if (isDark) {
            themeToggleBtn.classList.add('active');
        } else {
            themeToggleBtn.classList.remove('active');
        }
        
        const icon = themeToggleBtn.querySelector('.toggle-icon');
        if (icon) {
            icon.textContent = isDark ? '✓' : '';
        }
    }
}
